/**
 * Pre-Action Checkbox for Poker Reborn
 *
 * Toggle checkbox button for queuing actions before hero's turn.
 * Follows ActionButton pattern (center-anchored drawing).
 *
 * @see docs/screens/room/ACTION_PANEL.md - Pre-Action Checkboxes section
 */

import { Container, Graphics, Text } from 'pixi.js';
import type { PreActionType } from '@/types/game';

// =============================================================================
// STYLES
// =============================================================================

interface PreActionStyle {
  background: number;
  text: number;
  label: string;
}

const PRE_ACTION_STYLES: Record<PreActionType, PreActionStyle> = {
  fold: {
    background: 0x9e9e9e,
    text: 0xffffff,
    label: 'FOLD',
  },
  check: {
    background: 0x4caf50,
    text: 0xffffff,
    label: 'CHECK',
  },
  checkFold: {
    background: 0x4caf50,
    text: 0xffffff,
    label: 'CHECK/\nFOLD',
  },
  callAmount: {
    background: 0x2196f3,
    text: 0xffffff,
    label: 'CALL',
  },
  callAny: {
    background: 0x2196f3,
    text: 0xffffff,
    label: 'CALL\nANY',
  },
  allin: {
    background: 0xf44336,
    text: 0xffffff,
    label: 'ALL IN',
  },
};

const UNCHECKED_ALPHA = 0.3;
const CHECKED_ALPHA = 1.0;
const BORDER_RADIUS = 10;

// =============================================================================
// PRE-ACTION CHECKBOX
// =============================================================================

export class PreActionCheckbox extends Container {
  readonly preActionType: PreActionType;
  private buttonWidth: number;
  private buttonHeight: number;
  private style: PreActionStyle;

  private background: Graphics;
  private checkIndicator: Graphics;
  private labelText: Text;
  private amountText: Text;

  private isPressed = false;
  private isHovered = false;
  private _checked = false;

  /** Callback when clicked */
  onClick: (() => void) | null = null;

  constructor(
    preActionType: PreActionType,
    options: {
      width?: number;
      height?: number;
    } = {}
  ) {
    super();

    this.preActionType = preActionType;
    this.buttonWidth = options.width ?? 100;
    this.buttonHeight = options.height ?? 55;
    this.style = PRE_ACTION_STYLES[preActionType];

    // Background
    this.background = new Graphics();
    this.addChild(this.background);

    // Check indicator (small square in top-left corner)
    this.checkIndicator = new Graphics();
    this.addChild(this.checkIndicator);

    // Label text
    this.labelText = new Text({
      text: this.style.label,
      style: {
        fill: this.style.text,
        fontSize: 13,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
        align: 'center',
        lineHeight: 15,
      },
    });
    this.labelText.anchor.set(0.5);
    this.addChild(this.labelText);

    // Amount text (for Call $X)
    this.amountText = new Text({
      text: '',
      style: {
        fill: this.style.text,
        fontSize: 12,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.amountText.anchor.set(0.5);
    this.amountText.visible = false;
    this.addChild(this.amountText);

    // Initial draw
    this.draw();
    this.updateLayout();

    // Make interactive
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.on('pointerdown', this.handlePointerDown, this);
    this.on('pointerup', this.handlePointerUp, this);
    this.on('pointerover', this.handlePointerOver, this);
    this.on('pointerout', this.handlePointerOut, this);
    this.on('pointerupoutside', this.handlePointerUpOutside, this);
  }

  /** Get/set checked state */
  get checked(): boolean {
    return this._checked;
  }

  set checked(value: boolean) {
    if (this._checked !== value) {
      this._checked = value;
      this.draw();
    }
  }

  /** Set the amount to display (for callAmount type) */
  setAmount(amount: number): void {
    if (amount > 0) {
      this.amountText.text = `$${amount.toLocaleString()}`;
      this.amountText.visible = true;
    } else {
      this.amountText.visible = false;
    }
    this.updateLayout();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private draw(): void {
    this.background.clear();

    const alpha = this._checked ? CHECKED_ALPHA : UNCHECKED_ALPHA;
    const hoverBoost = this.isHovered && !this._checked ? 0.15 : 0;
    const pressBoost = this.isPressed ? 0.1 : 0;
    const finalAlpha = Math.min(1, alpha + hoverBoost + pressBoost);

    // Draw rounded rectangle
    this.background.roundRect(
      -this.buttonWidth / 2,
      -this.buttonHeight / 2,
      this.buttonWidth,
      this.buttonHeight,
      BORDER_RADIUS
    );
    this.background.fill({ color: this.style.background, alpha: finalAlpha });

    // Border: stronger when checked
    const borderAlpha = this._checked ? 0.6 : 0.3;
    this.background.setStrokeStyle({ width: 2, color: this.style.background, alpha: borderAlpha });
    this.background.stroke();

    // Check indicator
    this.drawCheckIndicator();
  }

  private drawCheckIndicator(): void {
    this.checkIndicator.clear();

    const indicatorSize = 12;
    const indicatorX = -this.buttonWidth / 2 + 6;
    const indicatorY = -this.buttonHeight / 2 + 6;

    // Small checkbox square
    this.checkIndicator.roundRect(indicatorX, indicatorY, indicatorSize, indicatorSize, 2);

    if (this._checked) {
      this.checkIndicator.fill({ color: 0xffffff, alpha: 0.9 });
      // Draw checkmark
      this.checkIndicator.setStrokeStyle({ width: 2, color: this.style.background });
      this.checkIndicator.moveTo(indicatorX + 2, indicatorY + 6);
      this.checkIndicator.lineTo(indicatorX + 5, indicatorY + 9);
      this.checkIndicator.lineTo(indicatorX + 10, indicatorY + 3);
      this.checkIndicator.stroke();
    } else {
      this.checkIndicator.fill({ color: 0x000000, alpha: 0.1 });
      this.checkIndicator.setStrokeStyle({ width: 1, color: 0xffffff, alpha: 0.4 });
      this.checkIndicator.stroke();
    }
  }

  private updateLayout(): void {
    if (this.amountText.visible) {
      this.labelText.y = -8;
      this.amountText.y = 10;
    } else {
      this.labelText.y = 0;
    }
  }

  private handlePointerDown(): void {
    this.isPressed = true;
    this.draw();
  }

  private handlePointerUp(): void {
    if (this.isPressed) {
      this.isPressed = false;
      this.draw();
      this.onClick?.();
    }
  }

  private handlePointerOver(): void {
    this.isHovered = true;
    this.draw();
  }

  private handlePointerOut(): void {
    this.isHovered = false;
    this.draw();
  }

  private handlePointerUpOutside(): void {
    this.isPressed = false;
    this.draw();
  }
}
